import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { didCancel } from 'ember-concurrency'
import { action } from '@ember/object'
import { waitForPromise } from '@ember/test-waiters'
import type RouterService from '@ember/routing/router-service'
import type RolloverStudentImportService from 'teacher-ui-client/services/rollover-student-import'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type { IntlService } from 'ember-intl'
import type Controller from '@ember/controller'

export default class ManageRolloverStudentImportValidateRoute extends Route {
  @service router!: RouterService

  @service rolloverStudentImport!: RolloverStudentImportService

  @service flashQueue!: FlashQueueService

  @service log!: Log

  @service intl!: IntlService

  get importJob() {
    return this.rolloverStudentImport.importJob
  }

  buildRouteInfoMetadata() {
    return {
      hideFooter: true,
    }
  }

  model() {
    // Only proceed if there's a file set, and this file hasn't undergone validation
    if (!this.importJob.file || this.importJob.valid !== undefined) {
      void this.router.replaceWith('manage.rollover.steps.students')
      return
    }

    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    return { subscriptionType }
  }

  setupController(controller: Controller, model: ModelFor<ManageRolloverStudentImportValidateRoute>) {
    super.setupController(controller, model)

    const { router, intl } = this

    // Fire off the validate CSV task, if one already isn't running
    if (this.rolloverStudentImport.validateCsv.isRunning) return

    waitForPromise(this.rolloverStudentImport.validateCsv.perform(model?.subscriptionType))
      .then((validateResult) => {
        const { importJob } = this
        if (importJob.valid) {
          this.flashQueue.clearMessages()
          this.flashQueue.addSuccess({ subtitle: intl.t('manage.studentCsv.validateSuccessful') })
          void router.replaceWith('manage.rollover.steps.students.import.confirm')
        } else if (importJob.validationErrors) {
          void router.replaceWith('manage.rollover.steps.students.import.invalid')
        } else {
          this.unexpectedErrorHandler(validateResult, 'student import validation')
        }
      })
      .catch((error) => {
        // should only reject for task cancel (eg, a cancel button) or network failure
        if (!didCancel(error)) {
          this.unexpectedErrorHandler(error, 'student import validation')
        }
      })
  }

  @action
  unexpectedErrorHandler(error: any, operation: string) {
    const detail = error?.detail || ''
    const { uuid } = this.log.error(`Unexpected error during ${operation}: ${detail}`, error)
    void this.router.replaceWith('manage.rollover.steps.students.import.error', uuid)
  }
}
